import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Form, Spinner } from 'reactstrap';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { AuthTags } from '~constants/authTags';

import { Button, TooltipPortal } from '~components';
import { faCopy } from '~components/icon';
import Icon from '~components/icon/Icon';
import {
  DEX_FIELD_COMPONENTS,
  DexFieldProps,
  DexFieldTag,
  DexFormValues,
  isDexFieldTag,
  MEAL_GATED_TAGS,
} from '~appointments/components/dexFields';

import {
  DexCopyableValues,
  DexDetails,
  DexDetailsBody,
  useGetDexDetailsQuery,
  useUpsertDexDetailsMutation,
} from '~appointments/actions/dexDetails';
import { useGetDexFieldMapQuery } from '~dex/actions/dex';

const hasTag = (tags: string[], tag: DexFieldTag) => tags.includes(tag);

// Mirrors toApiBody's own field-tag mask (below) so the copy action can't fill/attribute a field
// that isn't valid for the current DEX type, even if `copyable` and `field_tags` ever diverge (a
// stale cache, a race between fetch and a service-type change, or a backend bug).
const COPYABLE_FIELD_TAGS: Record<keyof DexCopyableValues, DexFieldTag> = {
  trip_distance_km: 'trip_distance',
  trip_travel_time_minutes: 'trip_travel_time',
  engagement_time_minutes: 'engagement_time',
};

// The meal-detail fields (texture_modified + meal counts) always show unless the tag list also
// carries meals_provided (Respite), in which case they only show once meals_provided is checked.
const mealDetailsVisible = (tags: string[], mealsProvided: boolean) => !hasTag(tags, 'meals_provided') || mealsProvided;

const buildValidationSchema = (fieldTags: string[]) => {
  const numericField = () =>
    Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? undefined : value))
      .min(0, 'Must be 0 or greater');

  const hasMealType = hasTag(fieldTags, 'meal_type');
  const mealGatedByProvided = hasTag(fieldTags, 'meals_provided');

  const mealCountField = () => {
    if (!hasMealType) return numericField();
    if (!mealGatedByProvided) return numericField().required('Required');
    return numericField().when('meals_provided', {
      is: true,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema,
    });
  };

  return Yup.object({
    subcontractor: hasTag(fieldTags, 'subcontractor')
      ? Yup.string().required('Subcontractor is required')
      : Yup.string(),
    session_location: hasTag(fieldTags, 'session_location')
      ? Yup.string().required('Session Location is required')
      : Yup.string(),
    total_cost: hasTag(fieldTags, 'total_cost')
      ? numericField().integer('Must be a whole number').required('Total Cost is required')
      : numericField(),
    extra_items: hasTag(fieldTags, 'extra_items')
      ? Yup.array().of(Yup.string()).min(1, 'Select at least one Extra Item')
      : Yup.array().of(Yup.string()),
    meal_type_main: mealCountField(),
    meal_type_light: mealCountField(),
    meal_type_dessert: mealCountField(),
    meal_type_beverage: mealCountField(),
    meal_type_snack: mealCountField(),
    engagement_time_minutes: numericField(),
    trip_distance_km: numericField(),
    trip_travel_time_minutes: numericField(),
  });
};

interface ComponentProps {
  /** The saved appointment's id. Absent in draft mode - the appointment does not exist yet. */
  appointmentId?: string | number;
  isActive?: boolean;
  /**
   * Draft mode: the same tab, rendered on an appointment that has not been saved yet, so
   * DEX data carried over by Create Similar can be reviewed and corrected BEFORE it is committed
   * rather than discovered afterwards on a record nobody looked at.
   *
   * Field tags come from the service type currently selected on the form instead of from a saved
   * appointment, so changing that selection re-masks the fields exactly as the saved tab does when
   * a service type is reassigned. The values are held by the parent and submitted with the create
   * request - there is nothing to PUT to yet - so the tab's own Save button is disabled.
   */
  isDraft?: boolean;
  serviceTypeId?: string | number | null;
  draftInitialValues?: Partial<DexDetailsBody> | null;
  onDraftChange?: (draft: { values: Partial<DexDetailsBody>; fieldTags: string[]; isValid: boolean }) => void;
}

// Shown on the draft tab's disabled Save button. The DEX values are saved as part of the
// appointment create request, so a separate save here would have nothing to write to.
const DRAFT_SAVE_TOOLTIP = 'Details will be saved when Appointment is created';

export const toFormValues = (data: Partial<DexDetailsBody> | null): DexFormValues => ({
  subcontractor: data?.subcontractor != null ? String(data.subcontractor) : '',
  volunteer: data?.volunteer ?? false,
  session_location: data?.session_location != null ? String(data.session_location) : '',
  overnight: data?.overnight ?? false,
  meals_provided: data?.meals_provided ?? false,
  meals_cooked_offsite: data?.meals_cooked_offsite ?? false,
  texture_modified: data?.texture_modified ?? false,
  meal_type_main: data?.meal_type_main != null ? String(data.meal_type_main) : '',
  meal_type_light: data?.meal_type_light != null ? String(data.meal_type_light) : '',
  meal_type_dessert: data?.meal_type_dessert != null ? String(data.meal_type_dessert) : '',
  meal_type_beverage: data?.meal_type_beverage != null ? String(data.meal_type_beverage) : '',
  meal_type_snack: data?.meal_type_snack != null ? String(data.meal_type_snack) : '',
  engagement_time_minutes: data?.engagement_time_minutes != null ? String(data.engagement_time_minutes) : '',
  trip_distance_km: data?.trip_distance_km != null ? String(data.trip_distance_km) : '',
  trip_travel_time_minutes: data?.trip_travel_time_minutes != null ? String(data.trip_travel_time_minutes) : '',
  total_cost: data?.total_cost != null ? String(data.total_cost) : '',
  extra_items: data?.extra_items ?? [],
});

// Only persist values for the tags the resolved DexID actually maps to; everything else is reset to
// its default so a service-type reassignment cannot leave stale field values behind.
export const toApiBody = (values: DexFormValues, fieldTags: string[]): Partial<DexDetailsBody> => {
  const has = (tag: DexFieldTag) => hasTag(fieldTags, tag);
  const includeMealDetails = has('meal_type') && mealDetailsVisible(fieldTags, values.meals_provided);
  const toNumber = (value: string) => (value !== '' ? Number(value) : null);
  return {
    subcontractor: has('subcontractor') ? values.subcontractor === 'true' : false,
    volunteer: has('volunteer') ? values.volunteer : false,
    session_location:
      has('session_location') && values.session_location !== '' ? Number(values.session_location) : null,
    overnight: has('overnight') ? values.overnight : false,
    meals_provided: has('meals_provided') ? values.meals_provided : false,
    meals_cooked_offsite: has('meals_cooked_offsite') ? values.meals_cooked_offsite : false,
    texture_modified: includeMealDetails ? values.texture_modified : false,
    meal_type_main: includeMealDetails ? toNumber(values.meal_type_main) : null,
    meal_type_light: includeMealDetails ? toNumber(values.meal_type_light) : null,
    meal_type_dessert: includeMealDetails ? toNumber(values.meal_type_dessert) : null,
    meal_type_beverage: includeMealDetails ? toNumber(values.meal_type_beverage) : null,
    meal_type_snack: includeMealDetails ? toNumber(values.meal_type_snack) : null,
    engagement_time_minutes: has('engagement_time') ? toNumber(values.engagement_time_minutes) : null,
    trip_distance_km: has('trip_distance') ? toNumber(values.trip_distance_km) : null,
    trip_travel_time_minutes: has('trip_travel_time') ? toNumber(values.trip_travel_time_minutes) : null,
    total_cost: has('total_cost') ? toNumber(values.total_cost) : null,
    extra_items: has('extra_items') && values.extra_items.length > 0 ? values.extra_items : null,
  };
};

const DexResetOnDirty: React.FC<{ dirty: boolean; onDirty: () => void }> = ({ dirty, onDirty }) => {
  useEffect(() => {
    if (dirty) onDirty();
  }, [dirty, onDirty]);
  return null;
};

// Publishes the draft form's current values to the parent so they can ride along in the create
// request. Lives inside <Formik> because that is the only place the live values exist - the parent
// owns the submit, not this tab.
const DexDraftPublisher: React.FC<{
  values: DexFormValues;
  fieldTags: string[];
  isValid: boolean;
  onDraftChange: (draft: { values: Partial<DexDetailsBody>; fieldTags: string[]; isValid: boolean }) => void;
}> = ({ values, fieldTags, isValid, onDraftChange }) => {
  const body = useMemo(() => toApiBody(values, fieldTags), [values, fieldTags]);
  useEffect(() => {
    // The tags travel with the body so the copy confirmation can list the same fields the user was
    // actually shown - toApiBody returns a complete row, defaulting every column this DEX type does
    // not use, and listing those reads as noise on a dialog asking someone to check the values.
    //
    // Validity travels with it because a draft has no submit of its own to gate on: the tab's Save
    // is disabled by design, so Formik's onSubmit never runs and the required-field rules that
    // protect a saved appointment would otherwise not apply to the one path that writes DSS data.
    onDraftChange({ values: body, fieldTags, isValid });
  }, [body, fieldTags, isValid, onDraftChange]);
  return null;
};

const AppointmentDexTab: React.FC<ComponentProps> = ({
  appointmentId,
  isActive = true,
  isDraft = false,
  serviceTypeId = null,
  draftInitialValues = null,
  onDraftChange,
}) => {
  const {
    data: dexData,
    isLoading,
    isFetching,
    isError,
  } = useGetDexDetailsQuery(
    // appointmentId is always defined when not in draft mode; the non-null assertion is confined
    // here rather than widening the query's own argument type.
    { appointmentId: appointmentId as string | number },
    { skip: !isActive || isDraft || !appointmentId },
  );

  // Draft mode resolves the same three mapping signals from the service type selected on the form,
  // since there is no appointment row to read them from yet.
  const {
    data: draftFieldMap,
    isLoading: isFieldMapLoading,
    isFetching: isFieldMapFetching,
    isError: isFieldMapError,
  } = useGetDexFieldMapQuery(
    { service_type_id: serviceTypeId as string | number },
    { skip: !isActive || !isDraft || !serviceTypeId },
  );

  // `skip` makes RTK Query select a distinct cache key, so `dexData` reverts to undefined the
  // instant the tab goes inactive — not just "not yet fetched". Retaining the last successfully
  // fetched value keeps `initialValues` stable across tab switches so Formik's enableReinitialize
  // doesn't wipe an unsaved draft when the user tabs away and back.
  //
  // The cache is bound to the appointmentId it was fetched for: if appointmentId changes (a
  // different appointment is selected) while the new query is still pending, `lastDexData` below
  // must not keep exposing the previous appointment's copyable/field-tag values - that stale data
  // could otherwise be copied or saved against the newly selected appointment. A same-appointment
  // tab-visibility toggle (isActive skip) doesn't change appointmentId, so the cache still serves
  // its original stability purpose there.
  const [dexDataCache, setDexDataCache] = useState<{
    appointmentId: string | number;
    data: DexDetails | null;
  } | null>(null);
  useEffect(() => {
    if (dexData !== undefined && appointmentId !== undefined) {
      setDexDataCache({ appointmentId, data: dexData });
    }
  }, [dexData, appointmentId]);
  // Explicit null check rather than optional chaining: appointmentId is optional in draft mode, and
  // `undefined === undefined` would otherwise match an empty cache against a draft.
  const lastDexData = dexDataCache !== null && dexDataCache.appointmentId === appointmentId ? dexDataCache.data : null;

  const [upsertDexDetails, { isLoading: isSaving, isSuccess: isSaveSuccess, isError: isSaveError, reset }] =
    useUpsertDexDetailsMutation();

  const savedFieldTags = lastDexData?.field_tags;
  const draftTags = draftFieldMap?.field_tags;
  const fieldTags = useMemo(
    () => (isDraft ? (draftTags ?? []) : (savedFieldTags ?? [])),
    [isDraft, draftTags, savedFieldTags],
  );
  const isExcluded = isDraft ? draftFieldMap?.is_excluded === true : lastDexData?.is_excluded === true;
  const dexFound = isDraft ? draftFieldMap?.dex_found === true : lastDexData?.dex_found === true;
  // "Copy from appointment" fills fields from values the appointment itself already holds (actual
  // times, distance travelled). A draft appointment has none of that recorded yet, so the action has
  // nothing to offer and is not rendered in draft mode.
  const copyable: DexCopyableValues = isDraft ? {} : (lastDexData?.copyable ?? {});

  // Which fields the last "Copy from appointment" click populated, so the save request can log
  // them as copied rather than manually entered. Reset per-appointment so switching appointments
  // (without an intervening unmount) can't attribute a stale copy to the wrong appointment.
  const [copiedFields, setCopiedFields] = useState<string[]>([]);
  useEffect(() => {
    setCopiedFields([]);
  }, [appointmentId]);

  const initialValues = useMemo(
    () => toFormValues(isDraft ? draftInitialValues : (lastDexData ?? null)),
    [isDraft, draftInitialValues, lastDexData],
  );
  const validationSchema = useMemo(() => buildValidationSchema(fieldTags), [fieldTags]);

  const handleSubmit = async (values: DexFormValues, { resetForm }: FormikHelpers<DexFormValues>) => {
    // A draft has nothing to PUT to - its values are submitted with the appointment create request
    // by the parent. The Save button is disabled in draft mode, so this is a belt-and-braces guard.
    if (isDraft || appointmentId === undefined) return;
    const result = await upsertDexDetails({
      appointmentId,
      ...toApiBody(values, fieldTags),
      copied_fields: copiedFields,
    });
    if (!('error' in result)) {
      resetForm({ values });
      setCopiedFields([]);
    }
  };

  if (isDraft && !serviceTypeId) {
    return (
      <Alert color="info" data-cy="dex-draft-no-service-type">
        Select a service type to see the DEX details for this appointment.
      </Alert>
    );
  }

  if (isDraft && (isFieldMapLoading || isFieldMapFetching) && !draftFieldMap) {
    return (
      <div className="p-4 text-center">
        <Spinner size="sm" /> Loading DEX details...
      </div>
    );
  }

  if (isDraft && isFieldMapError) {
    return <Alert color="danger">Failed to load DEX details. Please refresh and try again.</Alert>;
  }

  if (!isDraft && (isLoading || isFetching) && lastDexData === null) {
    return (
      <div className="p-4 text-center">
        <Spinner size="sm" /> Loading DEX details...
      </div>
    );
  }

  if (!isDraft && isError) {
    return <Alert color="danger">Failed to load DEX details. Please refresh and try again.</Alert>;
  }

  if (isExcluded) {
    return (
      <Alert color="info" data-cy="dex-excluded-message">
        This service type is excluded from DEX export.
      </Alert>
    );
  }

  if (!dexFound) {
    return (
      <Alert color="danger" data-cy="dex-config-missing-message">
        DEX configuration is missing for this appointment&apos;s service type.
      </Alert>
    );
  }

  const renderTag = (tag: string, fieldProps: DexFieldProps) => {
    if (!isDexFieldTag(tag)) return null;
    if (MEAL_GATED_TAGS.includes(tag) && !mealDetailsVisible(fieldTags, fieldProps.values.meals_provided)) {
      return null;
    }
    const FieldComponent = DEX_FIELD_COMPONENTS[tag];
    return <FieldComponent key={tag} {...fieldProps} />;
  };

  return (
    <div className="p-3 appointment-dex-tab" data-cy="appointment-dex-tab">
      {isSaveSuccess && (
        <Alert color="success" data-cy="dex-save-success">
          DEX details saved.
        </Alert>
      )}
      {isSaveError && (
        <Alert color="danger" data-cy="dex-save-error">
          Failed to save DEX details. Please try again.
        </Alert>
      )}
      {isFetching && lastDexData !== null && (
        <div className="text-muted small mb-2" data-cy="dex-refreshing-indicator">
          <Spinner size="sm" /> Refreshing DEX details...
        </div>
      )}

      <Formik
        enableReinitialize
        // Draft mode only: without it Formik reports isValid=true until something is touched, so
        // seeded source values that are invalid for the target's field set would publish as valid
        // and reach the create request. The saved tab keeps its existing behaviour, where errors
        // appear as the user interacts rather than immediately on open.
        validateOnMount={isDraft}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          dirty,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit: formikSubmit,
          isSubmitting,
          isValid,
          setFieldValue,
        }) => {
          // Only fields valid for this DEX type's resolved field tags - re-checked here rather than
          // trusted from `copyable` alone (the same defensive mask toApiBody applies at submit time)
          // - so a stale cache, a fetch/service-type-change race, or a backend bug can't offer a
          // field this DEX type doesn't use.
          const relevantCopyable = (Object.entries(copyable) as [keyof DexCopyableValues, number][]).filter(([field]) =>
            hasTag(fieldTags, COPYABLE_FIELD_TAGS[field]),
          );
          // Of those, only ones still blank in the form - a field the user already typed into (or a
          // previous copy already filled) is never overwritten.
          const fillableEntries = relevantCopyable.filter(([field]) => values[field] === '');
          let copyDisabledReason: string | null = null;
          if (relevantCopyable.length === 0) {
            copyDisabledReason = 'This appointment has no valid values to copy.';
          } else if (fillableEntries.length === 0) {
            copyDisabledReason = 'All copyable fields already have valid values.';
          }

          const handleCopyFromAppointment = () => {
            const copied = fillableEntries.map(([field, value]) => {
              setFieldValue(field, String(value));
              return field;
            });
            setCopiedFields((previous) => Array.from(new Set([...previous, ...copied])));
          };

          // A field the user edits after copying it is no longer purely "from the appointment
          // record" - drop it from copiedFields on the first keystroke so the save's audit log
          // attributes the eventual value to manual entry, not the copy action, once it's touched.
          const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            handleChange(event);
            const { name } = event.target;
            setCopiedFields((previous) => previous.filter((field) => field !== name));
          };

          return (
            <Form onSubmit={formikSubmit} data-cy="dex-form">
              <DexResetOnDirty dirty={dirty} onDirty={reset} />
              {isDraft && onDraftChange && (
                <DexDraftPublisher
                  values={values}
                  fieldTags={fieldTags}
                  isValid={isValid}
                  onDraftChange={onDraftChange}
                />
              )}
              {dirty && !isDraft && (
                <Alert color="warning" className="py-1 px-2 mb-3" data-cy="dex-unsaved-indicator">
                  Unsaved changes
                </Alert>
              )}

              {!isDraft && (
                <div className="mb-3">
                  <div
                    id="dex-copy-from-appointment-wrapper"
                    className="d-inline-block"
                    title={copyDisabledReason ?? undefined}
                  >
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCopyFromAppointment}
                      disabled={!!copyDisabledReason}
                      authTag={AuthTags.APPOINTMENTS.UPDATE}
                      dataCy="dex-copy-from-appointment-button"
                      aria-label={copyDisabledReason ? `Copy from appointment - ${copyDisabledReason}` : undefined}
                    >
                      <Icon className="me-2" icon={faCopy} />
                      Copy from appointment
                    </Button>
                    {copyDisabledReason && (
                      <TooltipPortal targetId="dex-copy-from-appointment-wrapper" content={copyDisabledReason} />
                    )}
                  </div>
                </div>
              )}

              {fieldTags.map((tag) =>
                renderTag(tag, { values, errors, touched, handleChange: handleFieldChange, handleBlur }),
              )}

              <div className="mt-4">
                {/* The wrapper carries the tooltip because a disabled button emits no mouse events
                    of its own - the same pattern the copy action above uses for its own disabled
                    state. */}
                <div
                  id="dex-save-button-wrapper"
                  className="d-inline-block"
                  title={isDraft ? DRAFT_SAVE_TOOLTIP : undefined}
                >
                  <Button
                    type="submit"
                    color="success"
                    disabled={isDraft || !dirty || isSubmitting || isSaving}
                    authTag={AuthTags.APPOINTMENTS.UPDATE}
                    dataCy="dex-save-button"
                    aria-label={isDraft ? `Save DEX Details — ${DRAFT_SAVE_TOOLTIP}` : undefined}
                  >
                    {isSaving ? 'Saving...' : 'Save DEX Details'}
                  </Button>
                  {isDraft && <TooltipPortal targetId="dex-save-button-wrapper" content={DRAFT_SAVE_TOOLTIP} />}
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default AppointmentDexTab;
